import React, { useMemo, useState } from "react";
import {
  Ic,
  Modal,
  inputStyle,
  btnPrimary,
  btnSecondary,
  btnGhost,
  showStyledAlert,
  showStyledConfirm,
} from "../components/UI";
import {
  addContributionType,
  updateContributionType,
  deleteContributionType,
} from "../utils/firestore";
import {
  formatContributionCurrency,
  sumActivitiesContributionTotal,
  sumActivitiesByContributionType,
  sumActivitiesContributionValueByType,
} from "../utils/activityContributions";

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#525264",
  marginBottom: 6,
};

export default function ContributionsPage({
  contributionTypes = [],
  activities = [],
  onRefresh,
  showToast,
  isMobile = false,
  canEdit = true,
}) {
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");

  const totalsByType = useMemo(() => sumActivitiesByContributionType(activities), [activities]);
  const totalUnits = useMemo(() => sumActivitiesContributionTotal(activities), [activities]);
  const valuesByType = useMemo(() => sumActivitiesContributionValueByType(activities), [activities]);
  const totalValue = useMemo(
    () => Object.values(valuesByType).reduce((sum, value) => sum + Number(value || 0), 0),
    [valuesByType]
  );

  const rows = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return contributionTypes
      .filter((item) => {
        if (!q) return true;
        return [item.name || "", item.unitLabel || ""].join(" ").toLowerCase().includes(q);
      })
      .map((item) => ({
        ...item,
        total: Number(totalsByType[item.name || ""] || 0),
        totalValue: Number(valuesByType[item.name || ""] || 0),
      }))
      .sort((a, b) => b.total - a.total || (a.name || "").localeCompare(b.name || "", "he"));
  }, [contributionTypes, search, totalsByType, valuesByType]);

  const topRows = rows.filter((row) => row.total > 0).slice(0, 4);

  const save = async (form) => {
    const payload = {
      name: (form.name || "").trim(),
      unitLabel: (form.unitLabel || "").trim() || "פריטים",
      unitValue:
        form.unitValue === "" || form.unitValue === null || form.unitValue === undefined
          ? null
          : Number(form.unitValue),
      active: form.active !== false,
    };
    if (!payload.name) {
      await showStyledAlert("יש להזין שם לסוג התרומה.");
      return;
    }
    try {
      if (modal?.type === "edit") {
        await updateContributionType(modal.item.id, payload);
        showToast("סוג תרומה עודכן");
      } else {
        await addContributionType(payload);
        showToast("סוג תרומה נוסף");
      }
      setModal(null);
      await onRefresh?.();
    } catch (e) {
      await showStyledAlert(`שגיאה בשמירת סוג תרומה: ${e.message}`);
    }
  };

  const remove = async (item) => {
    if (!await showStyledConfirm(`למחוק את סוג התרומה "${item.name}"?`)) return;
    try {
      await deleteContributionType(item.id);
      showToast("סוג תרומה נמחק");
      await onRefresh?.();
    } catch (e) {
      await showStyledAlert(`שגיאה במחיקת סוג תרומה: ${e.message}`);
    }
  };

  return (
    <div style={{ fontFamily: "'Heebo', 'Assistant', sans-serif", direction: "rtl" }}>
      <div style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #E8E8ED",
        padding: "16px 20px",
        marginBottom: 14,
        boxShadow: "0 1px 4px rgba(0,0,0,.03)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "Assistant", color: "#1A1A2E" }}>ניהול תרומות</div>
            <div style={{ fontSize: 13, color: "#8E8EA0" }}>הגדרת סוגי התרומה שניתן לבחור בעת הזנת פעילות</div>
          </div>
          {canEdit && <button
            onClick={() => setModal({ type: "add" })}
            style={{ ...btnPrimary, borderRadius: 12, padding: "9px 18px", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Ic.Plus /> סוג תרומה
          </button>}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5, minmax(0, 1fr))", gap: 10, marginBottom: 14 }}>
          {[
            { label: "סוגי תרומה", value: contributionTypes.length, color: "#2563EB", sub: "מוגדרים במערכת" },
            { label: "פעילים", value: contributionTypes.filter((item) => item.active !== false).length, color: "#16A34A", sub: "זמינים לבחירה" },
            { label: "סה\"כ תרומות", value: totalUnits, color: "#C2410C", sub: "מתוך כלל הפעילויות" },
            { label: "שווי כולל", value: formatContributionCurrency(totalValue) || "—", color: "#0F766E", sub: "לפי ערך ליחידה" },
            { label: "בשימוש", value: topRows.length, color: "#7C3AED", sub: "סוגים עם נתונים" },
          ].map((card) => (
            <div key={card.label} style={{ background: "#FCFCFE", border: "1px solid #ECECF2", borderRadius: 14, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "#8E8EA0", marginBottom: 6 }}>{card.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: card.color, fontFamily: "Assistant" }}>{card.value}</div>
              <div style={{ fontSize: 11, color: "#A3A3B5", marginTop: 3 }}>{card.sub}</div>
            </div>
          ))}
        </div>

        {topRows.length > 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {topRows.map((row) => (
              <span key={row.id} style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                borderRadius: 999,
                background: "#FFF7ED",
                border: "1px solid #FED7AA",
                color: "#9A3412",
                fontSize: 12,
                fontWeight: 700,
              }}>
                {row.name}: {row.total} {row.unitLabel || "פריטים"}{row.totalValue > 0 ? ` · ${formatContributionCurrency(row.totalValue)}` : ""}
              </span>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש סוג תרומה..."
            style={{ ...inputStyle, borderRadius: 12, minWidth: isMobile ? "100%" : 260 }}
          />
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E8E8ED", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {["שם", "יחידת מדידה", "ערך ליחידה", "סה\"כ כמות", "שווי מצטבר", "סטטוס", ""].map((h) => (
                <th key={h} style={{ padding: "11px 14px", fontSize: 11, fontWeight: 600, color: "#8E8EA0", textAlign: "right", background: "#FAFAFC", borderBottom: "1px solid #E8E8ED" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #F3F3F6", fontWeight: 700, color: "#1A1A2E" }}>{row.name}</td>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #F3F3F6", color: "#525264" }}>{row.unitLabel || "פריטים"}</td>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #F3F3F6", color: "#0F766E", fontWeight: 700 }}>
                  {row.unitValue != null ? formatContributionCurrency(row.unitValue) : "—"}
                </td>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #F3F3F6", color: "#9A3412", fontWeight: 700 }}>{row.total}</td>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #F3F3F6", color: "#0F766E", fontWeight: 700 }}>
                  {row.totalValue > 0 ? formatContributionCurrency(row.totalValue) : "—"}
                </td>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #F3F3F6" }}>
                  <span style={{
                    display: "inline-flex",
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    background: row.active !== false ? "#ECFDF5" : "#F5F5F7",
                    color: row.active !== false ? "#166534" : "#6B7280",
                    border: `1px solid ${row.active !== false ? "#BBF7D0" : "#E5E7EB"}`,
                  }}>
                    {row.active !== false ? "פעיל" : "מושבת"}
                  </span>
                </td>
                <td style={{ padding: "12px 14px", borderBottom: "1px solid #F3F3F6" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    {canEdit && <button onClick={() => setModal({ type: "edit", item: row })} style={{ ...btnSecondary, borderRadius: 10, padding: "7px 10px", fontSize: 12 }}>עריכה</button>}
                    {canEdit && <button onClick={() => remove(row)} style={{ ...btnGhost, borderRadius: 10, padding: "7px 10px", fontSize: 12, color: "#DC2626" }}>מחיקה</button>}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#A3A3B5" }}>
                  אין עדיין סוגי תרומה מוגדרים
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && canEdit && (
        <ContributionTypeModal
          initial={modal.type === "edit" ? modal.item : null}
          onClose={() => setModal(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function ContributionTypeModal({ initial = null, onClose, onSave }) {
  const [form, setForm] = useState({
    name: initial?.name || "",
    unitLabel: initial?.unitLabel || "פריטים",
    unitValue: initial?.unitValue ?? "",
    active: initial?.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Modal
      title={initial ? "עריכת סוג תרומה" : "סוג תרומה חדש"}
      onClose={onClose}
      footer={
        <>
          <button onClick={submit} disabled={saving} style={{ ...btnPrimary, borderRadius: 12, padding: "9px 18px" }}>
            {saving ? "שומר..." : "שמירה"}
          </button>
          <button onClick={onClose} style={{ ...btnSecondary, borderRadius: 12, padding: "9px 16px" }}>ביטול</button>
        </>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>שם התרומה</label>
        <input
          style={{ ...inputStyle, width: "100%", borderRadius: 12 }}
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="לדוגמה: חלות לשבת"
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>יחידת מדידה</label>
        <input
          style={{ ...inputStyle, width: "100%", borderRadius: 12 }}
          value={form.unitLabel}
          onChange={(e) => setForm((prev) => ({ ...prev, unitLabel: e.target.value }))}
          placeholder="לדוגמה: יחידות / מנות / עוגות"
        />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>ערך תרומה ליחידה בש"ח (אופציונלי)</label>
        <input
          type="number"
          min="0"
          step="0.01"
          style={{ ...inputStyle, width: "100%", borderRadius: 12 }}
          value={form.unitValue}
          onChange={(e) => setForm((prev) => ({ ...prev, unitValue: e.target.value }))}
          placeholder="לדוגמה: 35"
        />
      </div>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "#525264", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={form.active !== false}
          onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
          style={{ accentColor: "#C41E3A" }}
        />
        פעיל לבחירה בהזנת פעילות
      </label>
    </Modal>
  );
}
